import type { Worker } from "model/Tip";

interface GetWorkerNameOptions {
  readonly missingNameFallback?: string;
}

interface GetWorkerInitialsOptions extends GetWorkerNameOptions {
  readonly useDisplayNameFallback?: boolean;
}

export function getWorkerName(
  worker: Worker,
  options: GetWorkerNameOptions = {},
): string {
  const fullName = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
  if (fullName.length > 0) return fullName;

  if (worker.externalId && worker.externalId.trim().length > 0) {
    return worker.externalId;
  }

  return options.missingNameFallback ?? "-";
}

export function getWorkerInitials(
  worker: Worker,
  options: GetWorkerInitialsOptions = {},
): string {
  const firstInitial = worker.firstName?.[0] ?? "";
  const lastInitial = worker.lastName?.[0] ?? "";
  const initials = `${firstInitial}${lastInitial}`.trim();
  if (initials.length > 0) return initials.toUpperCase();

  if (!options.useDisplayNameFallback) {
    return "?";
  }

  const displayName = getWorkerName(worker, options);
  const displayInitials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return displayInitials.length > 0 ? displayInitials.toUpperCase() : "?";
}

export function getWorkerNameWithIdFallback(worker: Worker): string {
  return getWorkerName(worker, {
    missingNameFallback: `#${worker.workerId}`,
  });
}

export function getWorkerDisplayInitials(worker: Worker): string {
  return getWorkerInitials(worker, {
    useDisplayNameFallback: true,
  });
}
